
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ColorPickerSmall } from "global/components/ColourPicker";
import { CloseIcon, GlobalIconBase } from "global/strich_icons/StrichIcons";
import { copyCode } from "global/utilities/Copy";
import { useToast } from "global/hooks/useToast";

import "./QRCodePopup.css";



// Display and export an existing QR image without knowing which feature owns it
const QRCodePopup = ({ isOpen, onClose, imageUrl, value, valueLabel = "Link", title = "QR code", description = "", filename = "qr-code.png", loading = false, errorMessage = "" }) => {

    // Keep the chosen background separate from the stored transparent image
    const [backgroundColour, setBackgroundColour] = useState("#FFFFFF");
    const closeRef = useRef(null);
    const { toastSuccess, toastError } = useToast();

    // Reset the preview to a scannable white background on each opening
    useEffect(() => {

        if (isOpen) setBackgroundColour("#FFFFFF");

    }, [isOpen]);

    // Manage keyboard closing and background scrolling while the popup is visible
    useEffect(() => {

        if (!isOpen) return;

        // Preserve and temporarily disable background scrolling
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeRef.current?.focus();

        // Close the dialog when Escape is pressed
        const handleKeyDown = (event) => {

            if (event.key === "Escape") onClose();

        };

        document.addEventListener("keydown", handleKeyDown);

        // Restore the page's keyboard and scroll behaviour on close
        return () => {

            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = previousOverflow;

        };

    }, [isOpen, onClose]);



    // Close only when the user clicks outside the dialog
    const handleOverlayClick = (event) => {

        if (event.target === event.currentTarget) onClose();

    };



    // Copy the supplied address or URL without changing its underlying QR payload
    const handleCopy = () => {

        copyCode(valueLabel, value, toastSuccess, toastError);

    };



    // Combine the transparent QR and selected background into a downloadable PNG
    const handleSave = async () => {

        try {

            // Load the image with cross-origin access so canvas export is permitted
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.src = imageUrl;
            await image.decode();

            // Keep the downloaded image at the original QR resolution
            const canvas = document.createElement("canvas");
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            const context = canvas.getContext("2d");

            // Leave the result transparent when no background colour is selected
            if (backgroundColour) {
                context.fillStyle = backgroundColour;
                context.fillRect(0, 0, canvas.width, canvas.height);
            }

            // Draw crisp QR modules without interpolation or resizing
            context.imageSmoothingEnabled = false;
            context.drawImage(image, 0, 0);

            // Download the composed PNG without altering the stored QR image
            const link = document.createElement("a");
            link.href = canvas.toDataURL("image/png");
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();

        } catch (error) {

            // Report image loading or canvas export failures through the Portal
            toastError({ title: "Download failed", message: "The QR image could not be downloaded. Please try again." });

        }

    };



    // Render the same popup for email uploads, design links and product links
    if (!isOpen) return null;

    return createPortal(

        <div className="popup-overlay qr-code-popup__overlay" onClick={handleOverlayClick}>

            <div className="popup-container qr-code-popup__container" role="dialog" aria-modal="true" aria-label={title}>

                {/* Use the standard Portal popup header and close control */}
                <div className="popup__header">
                    <div className="popup__headerLeft">
                        <h2 className="popup__title">{title}</h2>
                    </div>
                    <div className="popup__headerRight">
                        <button ref={closeRef} type="button" className="popup__close" title="Close" onClick={onClose}>
                            <CloseIcon />
                        </button>
                    </div>
                </div>

                {/* Keep loading and API errors independent of the QR display */}
                <div className="popup-content qr-code-popup__content">

                    {loading ? (

                        <p>Loading QR code...</p>

                    ) : errorMessage ? (

                        <p role="alert">{errorMessage}</p>

                    ) : (

                        <>
                            {description && <p className="qr-code-popup__description">{description}</p>}

                            {/* Preview the chosen background beneath the original transparent PNG */}
                            {imageUrl ? (
                                <div className="qr-code-popup__preview-checkerboard">
                                    <div className="qr-code-popup__preview-background" style={{ backgroundColor: backgroundColour || "transparent" }}>
                                        <img className="qr-code-popup__image" src={imageUrl} alt={`QR code for ${value}`} />
                                    </div>
                                </div>
                            ) : (
                                <p>The QR image has not been generated yet.</p>
                            )}

                            {/* Allow the background colour and QR download to be controlled together */}
                            {imageUrl &&
                                <div className="qr-code-popup__colour-row">
                                    <span>Background: {backgroundColour || "Transparent"}</span>
                                    <ColorPickerSmall value={backgroundColour} onChange={setBackgroundColour} label="" title="QR background colour" allowNullOrTransparent={true} size="m" />
                                    <button type="button" className="qr-code-popup__save-button" onClick={handleSave}>
                                        Save PNG
                                    </button>
                                </div>
                            }

                            {/* Show the complete address or URL with a single copy control */}
                            <div className="qr-code-popup__value-row">
                                <div className="qr-code-popup__value-text">
                                    <span className="qr-code-popup__value-label">{valueLabel}</span>
                                    <span className="qr-code-popup__value">{value}</span>
                                </div>
                                <button type="button" className="qr-code-popup__icon-button" onClick={handleCopy} title={`Copy ${valueLabel.toLowerCase()}`} aria-label={`Copy ${valueLabel.toLowerCase()}`}>
                                    <GlobalIconBase size={19} strokeWidth={1.8}>
                                        <rect x="8" y="8" width="12" height="12" rx="2" />
                                        <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
                                    </GlobalIconBase>
                                </button>
                            </div>

                        </>

                    )}

                </div>

            </div>

        </div>,
        document.body

    );

};

export default QRCodePopup;
